import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { 
  FiChevronDown, 
  FiCheck, 
  FiLink, 
  FiRefreshCw,
  FiCircle,
  FiStar,
  FiLoader
} from 'react-icons/fi';
import { BrokerageConnection } from '../../types';
import { brokerageApi } from '../../api';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '../../utils/toast';
import { socketService } from '../../services/socketService';

interface BrokerSwitcherProps {
  selectedBrokerId: string | null;
  onBrokerChange: (brokerId: string | null) => void;
  className?: string;
}

// Custom hook to manage broker selection globally
export const useBrokerSelection = () => {
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(() => {
    // Load from localStorage on mount
    return localStorage.getItem('selectedBrokerId') || null;
  });

  const handleBrokerChange = (brokerId: string | null) => {
    setSelectedBrokerId(brokerId);
    if (brokerId) {
      localStorage.setItem('selectedBrokerId', brokerId);
    } else {
      localStorage.removeItem('selectedBrokerId');
    }
  };

  return { selectedBrokerId, handleBrokerChange };
};

const BrokerSwitcher: React.FC<BrokerSwitcherProps> = ({ 
  selectedBrokerId, 
  onBrokerChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [connections, setConnections] = useState<BrokerageConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingPrimary, setSettingPrimary] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Load connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track previous primary connection ID to detect changes
  const prevPrimaryConnectionIdRef = useRef<string | null>(null);
  
  // Auto-select primary broker if none selected OR if selected broker is no longer in connections
  // Also auto-select primary broker when primary broker changes (only if current selection was the old primary)
  useEffect(() => {
    if (!loading && connections.length > 0) {
      const primaryConnection = connections.find(c => c.isPrimary) || connections[0];
      const currentPrimaryId = primaryConnection?._id || null;
      
      // If no broker selected, default to primary
      if (!selectedBrokerId) {
        if (primaryConnection) {
          prevPrimaryConnectionIdRef.current = currentPrimaryId;
          onBrokerChange(primaryConnection._id);
        }
      } 
      // If selected broker no longer exists in connections, switch to primary
      else if (!connections.find(c => c._id === selectedBrokerId)) {
        if (primaryConnection) {
          prevPrimaryConnectionIdRef.current = currentPrimaryId;
          onBrokerChange(primaryConnection._id);
        }
      }
      // If primary broker changed AND current selected broker was the old primary, switch to new primary
      else if (primaryConnection && currentPrimaryId && prevPrimaryConnectionIdRef.current && 
               prevPrimaryConnectionIdRef.current !== currentPrimaryId && 
               selectedBrokerId === prevPrimaryConnectionIdRef.current) {
        prevPrimaryConnectionIdRef.current = currentPrimaryId;
        onBrokerChange(primaryConnection._id);
      } else if (currentPrimaryId) {
        // Update the ref even if we don't auto-select
        prevPrimaryConnectionIdRef.current = currentPrimaryId;
      }
    }
  }, [loading, connections, selectedBrokerId, onBrokerChange]);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await brokerageApi.getConnections();
      if (res.success && res.connections) {
        setConnections(res.connections);
      }
    } catch (error) {
      console.error('Failed to load broker connections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for brokerage connection updates via WebSocket
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleBrokerageUpdate = () => {
      console.log('🔄 Brokerage connection update received, refreshing connections...');
      loadConnections();
    };

    socket.on('brokerage:update', handleBrokerageUpdate);

    return () => {
      socket.off('brokerage:update', handleBrokerageUpdate);
    };
  }, [loadConnections]);

  const selectedConnection = connections.find(c => c._id === selectedBrokerId);

  const getAccountTypeLabel = (accountType?: string) => (
    accountType === 'paper' ? 'Demo' : 'Live'
  );

  const getBrokerLogo = useCallback((brokerageName: string, size: 'sm' | 'md' | 'lg' = 'md', connectionType?: string) => {
    const name = brokerageName.toLowerCase();
    const type = connectionType?.toLowerCase() || '';
    const sizeClasses = {
      sm: 'w-5 h-5',
      md: 'w-6 h-6',
      lg: 'w-8 h-8'
    };
    
    // Check for OANDA
    if (name.includes('oanda') || type === 'oanda') {
      return <img src="/oanda.png" alt="OANDA" className={`${sizeClasses[size]} object-contain`} />;
    }
    if (name.includes('webull')) {
      return <img src="/webull.png" alt="Webull" className={`${sizeClasses[size]} object-contain`} />;
    }
    if (name.includes('genesis') || type === 'genesis') {
      return <img src="/brokerages/genesisfx.png" alt="GenesisFX" className={`${sizeClasses[size]} object-contain rounded`} />;
    }
    if (name.includes('alpaca') || type === 'alpaca') {
      return <img src="/alpaca.png" alt="Alpaca" className={`${sizeClasses[size]} object-contain`} />;
    }
    // Fallback to emojis for other brokers
    if (name.includes('interactive')) return <span>🏦</span>;
    if (name.includes('td') || name.includes('ameritrade')) return <span>🟢</span>;
    if (name.includes('etrade')) return <span>⭐</span>;
    if (name.includes('fidelity')) return <span>💎</span>;
    if (name.includes('schwab')) return <span>💼</span>;
    if (name.includes('robinhood')) return <span>🪶</span>;
    return <span>📈</span>;
  }, []);

  // Memoized broker selection handler - automatically sets as primary when switching
  const handleBrokerSelect = useCallback(async (connectionId: string) => {
    // Automatically set as primary when switching accounts
    const selectedConnection = connections.find(c => c._id === connectionId);
    if (selectedConnection && !selectedConnection.isPrimary) {
      try {
        await brokerageApi.setPrimaryConnection(connectionId);
        showToast('success', 'Primary broker updated');
        await loadConnections();
      } catch (error) {
        console.error('Failed to set primary connection:', error);
        showToast('error', 'Failed to set primary broker');
      }
    }
    
    onBrokerChange(connectionId);
    setIsOpen(false);
    // Force refetch all dashboard queries by removing cache and invalidating
    queryClient.removeQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['trades'] });
    queryClient.invalidateQueries({ queryKey: ['brokerage', 'positions'] });
    queryClient.invalidateQueries({ queryKey: ['brokerage', 'orders'] });
  }, [onBrokerChange, queryClient, connections, loadConnections]);

  // Handle set as primary
  const handleSetPrimary = useCallback(async (connectionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from closing
    setSettingPrimary(connectionId);
    try {
      const result = await brokerageApi.setPrimaryConnection(connectionId);
      if (result.success) {
        showToast('success', 'Primary broker updated');
        // Reload connections to get updated primary status
        await loadConnections();
        // Update selected broker to the newly set primary
        onBrokerChange(connectionId);
        // Force refetch all dashboard queries by invalidating and removing cache
        queryClient.removeQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['trades'] });
        queryClient.invalidateQueries({ queryKey: ['brokerage'] });
      }
    } catch (error) {
      console.error('Failed to set primary connection:', error);
      showToast('error', 'Failed to set primary broker');
    } finally {
      setSettingPrimary(null);
    }
  }, [loadConnections, onBrokerChange, queryClient]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-lg ${className}`}>
        <FiRefreshCw className="h-4 w-4 animate-spin text-muted" />
        <span className="text-sm text-muted">Loading...</span>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-lg ${className}`}>
        <FiLink className="h-4 w-4 text-muted" />
        <span className="text-sm text-muted">No broker connected</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2.5
          bg-gradient-to-r from-surface to-surface-elevated
          border-2 border-border hover:border-primary/50
          rounded-xl shadow-sm hover:shadow-md
          transition-all duration-200 ease-out
          group
        `}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {selectedConnection ? (
            <>
              <div className="flex-shrink-0">
                {getBrokerLogo(selectedConnection.brokerageName, 'md', selectedConnection.connectionType)}
              </div>
              <div className="flex flex-col items-start min-w-0 flex-1">
                <div className="flex items-center gap-2 w-full min-w-0">
                  <span className="text-sm font-semibold text-foreground truncate max-w-[120px] min-w-0">
                    {selectedConnection.brokerageName}
                  </span>
                  {selectedConnection.isPrimary && (
                    <FiStar className="h-3.5 w-3.5 text-primary fill-primary flex-shrink-0" title="Primary" />
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <FiCircle
                    className={`h-2 w-2 ${
                      selectedConnection.accountType === 'paper'
                        ? 'fill-info text-info'
                        : 'fill-success text-success'
                    }`}
                  />
                  <span className="text-xs text-muted">
                    {getAccountTypeLabel(selectedConnection.accountType)}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <FiLink className="h-5 w-5 text-muted" />
              <span className="text-sm text-muted">Select Broker</span>
            </>
          )}
        </div>
        <FiChevronDown 
          className={`h-4 w-4 text-muted transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="
            absolute top-full left-0 right-0 mt-2 z-50
            bg-surface-elevated border-2 border-border rounded-xl
            shadow-xl shadow-black/20
            overflow-hidden
            animate-fadeIn
          "
          style={{ 
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-border bg-surface">
            <p className="text-xs font-semibold text-muted uppercase tracking-wider">
              Switch Broker Account
            </p>
          </div>

          {/* Options */}
          <div className="max-h-64 overflow-y-auto">
            {connections.map((connection) => {
              const isSelected = connection._id === selectedBrokerId;
              const isPrimary = connection.isPrimary;
              
              return (
                <div
                  key={connection._id}
                  className={`
                    relative flex items-center gap-2 px-3 py-3
                    hover:bg-primary/5 transition-colors
                    ${isSelected ? 'bg-primary/10' : ''}
                  `}
                >
                  <button
                    onClick={() => handleBrokerSelect(connection._id)}
                    className="flex-1 flex items-center gap-3 min-w-0"
                  >
                    <div className="flex-shrink-0">
                      {getBrokerLogo(connection.brokerageName, 'lg', connection.connectionType)}
                    </div>
                    
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-foreground truncate min-w-0">
                          {connection.brokerageName}
                        </span>
                        {isPrimary && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary/20 text-primary rounded flex-shrink-0">
                            PRIMARY
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          connection.accountType === 'paper'
                            ? 'bg-info/10 text-info'
                            : 'bg-success/10 text-success'
                        }`}>
                          {getAccountTypeLabel(connection.accountType)}
                        </span>
                        <span className="text-xs text-muted">
                          ${(connection.accountBalance || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {isSelected && (
                      <FiCheck className="h-5 w-5 text-primary flex-shrink-0" />
                    )}
                  </button>

                </div>
              );
            })}
          </div>

          {/* Footer - Add Broker */}
          <div className="px-3 py-2 border-t border-border bg-surface">
            <button
              onClick={() => {
                setIsOpen(false);
                // Navigate to connect brokerage tab
                const event = new CustomEvent('navigateToTab', { detail: 'connect' });
                window.dispatchEvent(event);
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <FiLink className="h-4 w-4" />
              <span>Add Another Broker</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Export memoized component to prevent unnecessary re-renders
export default memo(BrokerSwitcher);
